import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRequest } from "ahooks";
import { Helmet } from "react-helmet";
import JDIProductCardV2 from "@/common-components-src/js/productCard_V2/index";
import UKToast from "@/common-components-src/components/UKToast/index";
import UKPageError from "@/common-components-src/components/UKPageError";
import UKPageLoading from "@/common-components-src/components/UKPageLoading";
import UKPageNothing from "@/common-components-src/components/UKPageNothing";
import TooltipCom from "../components/TooltipCom";
import Pagination from "../../order/components/pagination";
import { fetchGetBrowseHistoryList, fetchDeleteBrowseHistory } from "@/app/csr-src/apis/browsingHistory/browsingHistory.api";
import type { BrowsingHistoryItem, ProductInfo, promoTagInfoType, DeleteBrowseHistoryReq } from "@/app/csr-src/apis/browsingHistory/browsingHistory.interface";
import "./index.scss";
import EmptyBox from "@/app/csr-src/assets/icons/EmptyBox.svg";
import { Checkbox, Button } from "@mui/material";
import { sendPv, EXPOSURE_RECORD, IExpoTrackParamsRecord, sendClickTrack } from "@/tracks/25141";
import { EXPCONFIG_FAST, EXPCONFIG_DELAY } from "@/common-components-src/const/point";

const BrowsingHistory = () => {
  const { t } = useTranslation();
  const [isNothing, setIsNothing] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpenEdit, setIsOpenEdit] = useState(false);
  const [page, setPage] = useState({
    pageSize: 12, // 
    currentPage: 1,
  });
  const [currencySymbol, setCurrencySymbol] = useState("£");
  const [data, setData] = useState([]);
  const [lastPage, setLastPage] = useState(false);
  const [selectedSKUList, setselectedSKUList] = useState<{ skuId: string }[]>([]);
  const [dataDate, setDataDate] = useState({} as any); // 按时间重组数据列表
  const [total, setTotal] =useState(0) // 总条数
  // 点击批量删除按钮
  const clickAllDelete = async () => {
    sendClickTrack.MyJD_History_Delete({ skuid: selectedSKUList.map((item) => item.skuId)?.join("#") || "-100" });
    // 校验选中列表是否为空
    if (!selectedSKUList?.length) {
      UKToast.show({
        content: "Please select at least one product", // 弹窗内容
        icon: "warning", // 可选：success, fail, loading, warn, info
        duration: 1000,
      });
      return;
    }
    const params = {
      skuList: selectedSKUList,
    };
    deleteBrowsingHistory(params);
  };
  // 单个删除
  const clickDelete = async (item: BrowsingHistoryItem) => {
    const params: DeleteBrowseHistoryReq = {
      skuList: [{ skuId: item.skuId as number }],
    };
    deleteBrowsingHistory(params);
  };
  const deleteBrowsingHistory = async (params: DeleteBrowseHistoryReq) => {
    const res = await fetchDeleteBrowseHistory(params);
    if (res.code === "200") {
      UKToast.show({
        content: t("common.message.deleteSuccessful"), // 弹窗内容
        icon: "success", // 可选：success, fail, loading, warn, info
        duration: 3000,
        exposureTrack: EXPOSURE_RECORD.MyJD_History_Delete_Confirm,
      });
      setIsOpenEdit(false);
      setPage({
        pageSize: 12,
        currentPage: 1,
      });
      setTotal(0)
      
      getBrowseHistory();
    } else {
      UKToast.show({
        content: res.msg, // 弹窗内容
        icon: "fail", // 可选：success, fail, loading, warn, info
        duration: 3000,
      });
    }
  };
  // 分页change事件
  const pagesChangeHandler = (pageNum: number) => {
    window.scrollTo(0, 0); // 滚动到顶部
    setPage({
      ...page,
      pageSize: 12,
      currentPage: pageNum,
    });
    setTotal(0)
  };

  const getBrowseHistory = async () => {
    setIsLoading(true);
    setIsError(false);
    const { pageSize, currentPage } = page;
    const params = {
      pageSize,
      currentPage,
      totalCountSwitch: true,
    };
    const res = await fetchGetBrowseHistoryList(params);
    if (res.code === "200" && res.data) {
      const { data, total, localInfo } = res.data;
      if (data && data.length) {
        // 格式化数据结构
        const new_data: any = []; // 处理成商卡需要的字段
        data.forEach((v: any) => {
          const new_item = getProductData(v);
          new_data.push(new_item);
        });
        const dataDate: any = new_data.reduce((group: any, item: any) => {
          const { displayDate } = item;
          group[displayDate] = group[displayDate] ?? [];
          group[displayDate].push(item);
          return group;
        }, {});
        setDataDate(dataDate);
        setData(new_data || []);
        setCurrencySymbol(localInfo?.currencySymbol);
        setTotal(total)
        setLastPage(lastPage);
        setIsNothing(false);
        setIsLoading(false);
      } else {
        setIsNothing(true);
      }
      setIsError(false);
    } else {
      setIsError(true);
    }
    setIsLoading(false);
  };
  // 点击单个商品删除按钮防抖
  const { run: tooltipClickDelete } = useRequest(clickDelete, {
    debounceWait: 500,
    debounceLeading: true,
    manual: true,
  });
  // 点击删除选中商品按钮防抖
  const { run: handleClickDelete } = useRequest(clickAllDelete, {
    debounceWait: 500,
    debounceLeading: true,
    manual: true,
  });
  const getProductData = (item: any) => {
    /* 
      处理营销标签 promoTagInfo 
    */
    const promotions: any = [];
    if (item?.promoTagInfo) {
      const promoTagInfo = item.promoTagInfo;
      Object.keys(promoTagInfo).forEach((key) => {
        promotions.push({ showStyle: promoTagInfo[key] });
      });
    }
    // 获取销量;
    const { promoTagSet, promoTagInfo } = item;
    let saleNumData: any = {};
    let saleNum = null;
    if (promoTagSet?.includes("15")) {
      saleNumData = JSON.parse(promoTagInfo[15]);
      saleNum = saleNumData.text;
    }
    const productData = {
      type: "2",
      contentStyle: { marginTop: "16px" },
      skuName: item.skuName,
      skuImgKeyParams: {
        imgUrlKey: item.imgUrl,
        width: 240,
        height: 240,
      },
      skuId: item.skuId,
      skuImgStyle: { height: "240px" },
      realPrice: item.realPrice,
      originalPrice: item.originalPrice,
      currency: currencySymbol,
      // saleInfo: item.saleInfo, // 规格 Todo前
      shouldShowScore: true,
      score: item.score,
      saleNum: saleNum,
      // shouldShowCart: true,
      shouldShowCart: !isOpenEdit,
      onClickProduct: !isOpenEdit
        ? null
        : () => {
            return;
          },
      isOutOfStock: item.tagList?.includes(4) ? true : false, // 是否展示无货
      isRemoved: item.tagList?.includes(5) ? true : false,
      // promotions: promotions?.length ? promotions : [], // 营销标签数组
      displayDate: item.displayDate,
    };
    return productData;
  };
  const onRefresh = (val: any) => {
    const { selected, skuId } = val;
    const selectedSKUs: any = [];
    data.forEach((v: any, i: any) => {
      if (v.skuId === skuId) {
        v.selected = selected;
      }
      v.selected && selectedSKUs.push({ skuId: v.skuId });
    });

    setselectedSKUList(selectedSKUs);
    setData(data);
  };
  // 跳转商详
  const formatProductLink = (skuId: string, skuName?: string, inner: boolean = true, limit = 6) => {
    // skuName 过滤非英文、数字及空格
    skuName = skuName
      ?.replace(/[^a-zA-Z0-9\s]/g, "")
      ?.split(" ")
      ?.slice(0, limit)
      ?.join("-");
    return skuName ? `/dp/${skuName}/${skuId}` : `/dp/${skuId}`;
  };
  const handdleClickProduct = (data: any) => {
    // 编辑态-选中卡片
    if (isOpenEdit) {
      console.log(isOpenEdit);
      // onRefresh(data);
      return;
    } else {
      // 非编辑态-跳转商详
      const { skuId, skuName } = data;
      const url = formatProductLink(skuId, skuName);
      location.href = url;
    }
  };
  const upDateEditStatus = () => {
    isOpenEdit ? sendClickTrack.MyJD_History_Edit() : sendClickTrack.MyJD_History_Done();
    const currentIsOpenEdit = !isOpenEdit;
    setIsOpenEdit(currentIsOpenEdit);
  };

  useEffect(() => {
    getBrowseHistory();

    sendPv.MyJD_History_Main();
  }, []);

  useEffect(() => {
    getBrowseHistory();
  }, [page.currentPage]);

  return (
    <div className="browsingHistory">
      <Helmet>
        <title>Shopping activity | Joybuy</title>
        <meta
          name="description"
          content="Joybuy offers the best online shopping experience with fast shipping and delivery guarantee on all orders. Shop now for unbeatable deals on top-quality products."
        />
      </Helmet>
      <div className="title_container">
        <div className="title">{t("common.pageName.browsingHistory")}</div>
        {!isNothing && !isError && !isLoading && (
          <div className="EditBtns">
            {isOpenEdit ? (
              <>
                <Button
                  className="EditBtn"
                  onClick={() => upDateEditStatus()}
                  exptag={"exp|" + EXPOSURE_RECORD.MyJD_History_Done_Expo}
                  data-exptag-config={EXPCONFIG_DELAY}
                >
                  {t("common.btnText.done")}
                </Button>
                <Button
                  className="deleteBtn"
                  onClick={() => clickAllDelete()}
                  exptag={"exp|" + EXPOSURE_RECORD.MyJD_History_Delete_Expo}
                  data-exptag-config={EXPCONFIG_FAST}
                  data-exptag-json-param={JSON.stringify({
                    skuid: selectedSKUList.map((item) => item.skuId)?.join("#") || "-100",
                  } as IExpoTrackParamsRecord["MyJD_History_Delete_Expo"])}
                >
                  {t("common.btnText.delete")}
                </Button>
              </>
            ) : (
              <Button
                className="EditBtn"
                onClick={() => upDateEditStatus()}
                exptag={"exp|" + EXPOSURE_RECORD.MyJD_History_Edit_Expo}
                data-exptag-config={EXPCONFIG_FAST}
              >
                {t("common.btnText.edit")}
              </Button>
            )}
          </div>
        )}
      </div>
      {isNothing && (
        <div className="errorPage">
          <UKPageNothing style={{ marginTop: "60px" }} text="There's nothing more" />
        </div>
      )}
      {isError && (
        <div className="errorPage">
          <UKPageError style={{ marginTop: "60px" }} clickTryAgain={() => getBrowseHistory()} />
        </div>
      )}
      {isLoading && (
        <div className="errorPage">
          <UKPageLoading />
        </div>
      )}
      {!isNothing && !isError && !isLoading && (
        <div>
          {dataDate && JSON.stringify(dataDate) !== "{}" ? (
            <div className="itemList">
              {Object.keys(dataDate).map((key: string, index) => {
                return (
                  <div key={JSON.stringify(key)}>
                    <div className="displayDate">{key}</div>
                    <div className="itemBox">
                      {(dataDate as any) &&
                        dataDate[key].map((v: any, i: any) => {
                          const commonTrackParams = {
                            index: i,
                            skuid: v.skuId,
                            firprice: v.realPrice,
                            firpricetype: "83" as "83",
                            secprice: v.originalPrice,
                            secpricetype: "11" as "11",
                            freqTag: "",
                            couponInfo: "",
                            single_promotion: "",
                            amount_promotion: "",
                            time_label: "",
                          };

                          return (
                            <div key={JSON.stringify(v)} className="ProductCardItem">
                              <div className="operationBox">
                                {/* 复选框 */}
                                {isOpenEdit ? (
                                  <Checkbox
                                    sx={{
                                      padding: 0,
                                      margin: "0 8px 0 0",
                                      color: "#E0E1E5",
                                      "&.Mui-checked": {
                                        color: "rgba(204, 12, 28, 1)",
                                      },
                                    }}
                                    key={v.skuId}
                                    checked={!!v.selected}
                                    onClick={(event) => {
                                      const target = event.target as HTMLInputElement;
                                      onRefresh({
                                        skuId: v?.skuId,
                                        selected: target.checked,
                                      });
                                    }}
                                  />
                                ) : (
                                  <div></div>
                                )}
                                {!isOpenEdit ? <TooltipCom ProductItem={v} deleteFn={() => clickDelete(v)} /> : null}
                              </div>

                              <JDIProductCardV2
                                productData={{
                                  ...v,
                                  trackingPointParams: {
                                    productExpConfig: {
                                      event_id: EXPOSURE_RECORD.MyJD_History_Product_Expo,
                                      config: EXPCONFIG_FAST,
                                      json_param: commonTrackParams as IExpoTrackParamsRecord["MyJD_History_Product_Expo"],
                                    },
                                    cartExpConfig: {
                                      event_id: EXPOSURE_RECORD.MyJD_History_Product_Addtocart_Expo,
                                      config: EXPCONFIG_FAST,
                                      json_param: commonTrackParams as IExpoTrackParamsRecord["MyJD_History_Product_Addtocart_Expo"],
                                    },
                                    productClickPoint: sendClickTrack.MyJD_History_Product(commonTrackParams),
                                    cartClickPoint: sendClickTrack.MyJD_History_Product_Addtocart(commonTrackParams),
                                  },
                                }}
                                // onClick={(e: any) => {
                                //   e.stopPropagation();
                                //   handdleClickProduct(v);
                                // }}
                              />
                            </div>
                          );
                        })}
                    </div>
                  </div>
                );
              })}
              {total > 12 ? (
                <div className="pagination_wrap">
                  <Pagination currentPage={page.currentPage} totalPages={Math.ceil(total / page.pageSize)} handlePageChange={pagesChangeHandler} />
                </div>
              ) : null}
            </div>
          ) : (
            <div className="emptyBox">
              <EmptyBox className="emptyBoxIcon" />
              <p className="emptyBoxMsg">There&apos;re nothing more </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
export default BrowsingHistory;
